/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2023 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of subscription software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 * */

/**
  * Common Plugin
  *
  * @author Mohamed yasar arabath M
  */

import React from 'react';
import Framework from '@greenville/framework';
import { getSnapshot } from 'mobx-state-tree';
import {
  AUTH_HOME,
  MLM_AUTH_HOME,
  BUNDLED_PLATFORM,
  BRAZE_TARGET_AREA,
  COURSE_TYPE_SMS,
  COURSE_TYPE_REGISTRAR,
  CHAPTER_ID_QUERY_PARAM,
  EREADER,
  EREADER_CATEGORY,
  EVENT_EREADEREVENT,
  EREADER_ACTION_ACCESS_ETEXT,
  PRODUCT_MODEL,
  SOURCE_OF_EREADER_LAUNCH,
  LMS,
  REDIRECTURL,
  BRAZE_IDENTIFY_USER,
  CHANNEL_SECTION_FILTERS,
  BRAZE_EREADER_COMPONENTS,
  DRAWER_STATE_OPEN,
  DRAWER_PANEL_AUDIO,
  DRAWER_PANEL_CHANNELS,
  DRAWER_PANEL_STUDY,
  DRAWER_PANEL_NOTEBOOK,
  AITutorLoaded,
  GO_TO_PAGENO_QUERY_PARAM,
  SOURCE_FROM,
  IS_CONSOLE,
  CUSTOM_COLLECTIONS,
  QR_CODE_SERVICE_FETCHED,
  NOT_AVAILABLE,
  USER_ACCESS_TYPE_VALUES,
  USER_AB_TEST_LABEL,
  PRINT_PAGE_ILLUSTRATION,
  HERO_BANNER_TYPES,
  VARIANT_V1,
  DEFAULT_TEMPLATE,
  CHANNEL_FILTER,
  LD_EVENT_NAME,
  LOCATION_IN_APP,
  DEVICE_LIST_REQUEST_SUCCESS,
  IES_DEVICE_LIST_REQUESTED,
  THREAT_STATUS_SUCCESS,
  ACCOUNT_LOCK_SUCCESS,
  OTP_MODAL,
  LOCK_ACCOUNT_EVENT_NAME,
  LOCK_ACCOUNT_EVENT_ACTION,
  LOCK_ACCOUNT_EVENT_LABEL,
  SHOW_DEVICE_MANAGEMENT_POPUP,
  DEVICE_REGISTER_SUCCESS,
  DEVICE_VIOLATION_SUCCESS,
  SEND_OTP,
  INITIATE_POINT_QUERY_PARAM,
  ACTIVE,
  ENTITLEMENTLEVEL_STUDY,
  STUDY_PREP_ROUTES,
  AH_GLOBAL_NAV_EVENT_LABEL,
  AH_NO_GLOBAL_NAV_EVENT_LABEL,
  QR_CODE_EVENT,
  LMS_PDP_REDIRECT_EVENT,
  ASSIGNMENT_ID_QUERY_PARAM,
  ETEXT_PREFERENCE_SOURCE
} from '../constants';
import CommonUtils from '../utils/CommonUtils';
import PathUtils from '../utils/PathUtils';
import env from '../env';
import BrazeUtils from '../utils/BrazeUtils';
import PendoUtils from '../utils/PendoUtils';
import PrintMessage from '../components/printmessage/PrintMessage';
import QRCodePrintMessage from '../components/printmessage/QRCodePrintMessage';
import BrazeContainer from '../../modules/braze/BrazeContainer';
import MixpanelUtils from '../utils/MixpanelUtils';
import AssignmentFetcher from '../components/AssignmentsFetcher';

export default class CommonPlugin {
  constructor(context, location = AUTH_HOME) {
    this.commonContext = context;
    this.location = location;
    this.subscribeListener = new Map();
    this.redirectUrl = PathUtils.getQueryParameterbyName(REDIRECTURL);
    this.isFromDeepLink = CommonUtils.isQueryParamAvailable(LMS, 'y') || CommonUtils.isIntergratedMLMLaunch(); // ereader launch from deeplink
    this.deviceManagementInitiated = false;
    this.initializeMixPanelEventListener();
    this.init();
    this.assignmentFetcher = new AssignmentFetcher(context, location);
  }

  /**
   * Mixpanel start session recording
   *
   */
  // eslint-disable-next-line class-methods-use-this
  mixpanelStartSessionRecording = () => {
    MixpanelUtils.startSessionRecordTimer();
  }

  /**
   * Mixpanel end session recording
   *
   */
  // eslint-disable-next-line class-methods-use-this
  mixpanelEndSessionRecording = () => {
    MixpanelUtils.stopSessionRecordTimer();
  }

  /**
   * Handle channel card click event
   *
   */
  handleChannelCardClick = () => {
    const { featureFlagsClient } = this.commonContext.props;

    featureFlagsClient.track(LD_EVENT_NAME.CLICK_CHANNEL_CARD_ELEMENT);
  };

  init = () => {
    const {
      isGlobalNavEnabled
    } = this.commonContext.props || {};

    if (this.location === EREADER && isGlobalNavEnabled) {
      // Reset isDeviceMangementActivate flag whenever
      Framework.isDeviceMangementActivate = false;
    }

    Framework.getEventManager().on(QR_CODE_SERVICE_FETCHED, (response) => {
      this.updateQRCode(response);
    });

    Framework.getEventManager().on(LMS_PDP_REDIRECT_EVENT, () => {
      const { match } = this.commonContext.props;
      CommonUtils.handleLMSFlow(match, window.location.href, this.commonContext.state.flags);
    });

    if (this.location === MLM_AUTH_HOME || this.location === AUTH_HOME) {
      this.subscribe('onDisplayOfQRCodeTestVariants', () => {
        const { user } = this.commonContext.props || {};
        const userStore = getSnapshot(user);
        const isUserFetched = userStore && userStore.gpsSubscriptions;
        const { isUserPreferenceFetched, userFeatureFlagsFetched } = this.commonContext.state || {};

        if (isUserFetched && isUserPreferenceFetched && userFeatureFlagsFetched) {
          const enableQrCodeTestVariants = this.commonContext.heroDashboardPlugin.showQrCodeTestVariants();

          if (!enableQrCodeTestVariants) {
            return false;
          }

          CommonUtils.fetchUIDQRcode(QR_CODE_EVENT.HEADER_FIELD, false);

          return true;
        }

        return false;
      });


      // template and LD and heroSet success
      this.subscribe('templateAnduserPrefAndHeroSetAndAssignmentFetch', () => {
        let success = false;
        const {
          templateDataFetch,
          userFeatureFlagsFetched,
          flags,
          isAssignmentsFetched
        } = this.commonContext.state;
        const {
          featureFlagsClient
        } = this.commonContext.props;
        const { herobanner } = this.commonContext.props;
        const bannerType = herobanner.bannerType;

        const isAssignmentsFetchedOrAetNotEnabled = herobanner.isAETEnabled ? isAssignmentsFetched : true;

        if (templateDataFetch && userFeatureFlagsFetched && !!bannerType && isAssignmentsFetchedOrAetNotEnabled) {
          // featureFlagsEvaluationByUser for mixpanel session start
          const template = CommonUtils.getCurrentScreenEventLabel(this.commonContext.state.flags);

          this.commonContext.featureFlagsEvaluationByUser({ template }, (isError) => {
            if (!isError) {
              if (featureFlagsClient.variation('enableMixpanelRecord', false)) {
                this.mixpanelStartSessionRecording();
              }
            }
          });
          CommonUtils.logABTestingEvent(
            this.isIntegratedUI()
              ? AH_GLOBAL_NAV_EVENT_LABEL
              : AH_NO_GLOBAL_NAV_EVENT_LABEL,
            null
          );

          CommonUtils.logABTestingEvent(
            flags.enableSampleExams
              ? USER_AB_TEST_LABEL.SYLLABUS_UPLOAD_WITH_SAMPLETESTS
              : USER_AB_TEST_LABEL.SYLLABUS_UPLOAD_WO_SAMPLETESTS,
            null
          );

          success = true;
        }

        return success;
      });
    }

    if (this.location === EREADER) {
      this.subscribe('devicesAndProductAndLDFetch', () => {
        let success = false;
        const product = this.getProduct();
        const { userFeatureFlagsFetched } = this.commonContext.state;
        if (userFeatureFlagsFetched && product.id && this.deviceManagementInitiated) {
          this.deviceManagemenFlowOnEreader();
          success = true;
        }
        return success;
      });
      this.subscribe('VRApploadedAndProductAndLDFetch', () => {
        let success = false;
        const product = this.getProduct();
        const { userFeatureFlagsFetched, flags, isPageIdSetOnNavChange } = this.commonContext.state;
        if (userFeatureFlagsFetched
          && flags.enableMixpanelRecord
          && product.id
          && isPageIdSetOnNavChange
        ) {
          this.mixpanelStartSessionRecording();
          success = true;
        }
        return success;
      });
      this.subscribe('LDUserFlagsAndProductFetchedAndUserFetched', () => {
        const { userFeatureFlagsFetched } = this.commonContext.state;
        const product = this.getProduct();
        const { user } = this.commonContext.props;
        const isUserFetched = user && user.gpsSubscriptions;

        if (userFeatureFlagsFetched && product?.id && isUserFetched) {
          this.assignmentFetcher.fetchAll();

          return true;
        }

        return false;
      });
      Framework.getEventManager().on(DEVICE_LIST_REQUEST_SUCCESS, () => {
        this.deviceManagementInitiated = true;
        this.onBroadCast();
      });
    }
  }

  /**
   * Subscribe event and their callback business logic
   *
   * @param {*} name
   * @param {*} callBack
   */
  subscribe = (name, callBack) => {
    if (name && typeof callBack === 'function') {
      this.subscribeListener.set(name, callBack);
    }
  }

  /**
   * Listen subscribe based on state change.
   * CallBack return true then it will execute the event only one time
   *
   */
  onBroadCast = () => {
    if (this.subscribeListener.size > 0) {
      this.subscribeListener.forEach((callBack, key) => {
        if (callBack()) {
          this.subscribeListener.delete(key);
        }
      });
    }
  }

  /**
   * To render Notification banner below header
   *
   * @returns
   */
  renderNotificationBanner = () => {
    let renderComp = null;
    const notificationBannerData = this.commonContext.brazePlugin.getContentCardByTargetArea(BRAZE_TARGET_AREA.AUTHHOME_HEADER_NOTIFICATION);
    if (notificationBannerData) {
      renderComp = (
        <BrazeContainer
          data={notificationBannerData?.data}
          callbackForBraze={notificationBannerData?.callbackForBraze}
        />
      );
    }

    return renderComp;
  }

  /**
   * subscribe for api data
   * @param {*} response
   */

  updateQRCode = (response) => {
    const {
      jwtToken, uId, isError = false, closeConnection = false, shouldOpenModal = true
    } = response;
    let url = '';
    if (jwtToken && uId) {
      const constructedQueryParams = new URLSearchParams(this.commonContext.nativeAppBannerPlugin.constructQRCodeQueryParam(jwtToken, uId));
      url = `${env.APPSFLYER_BASE_URL}?${constructedQueryParams.toString()}`;
    }

    if (isError || closeConnection) {
      if (CommonUtils.controller) {
        CommonUtils.controller.abort();
      }
    }

    if (closeConnection) {
      this.commonContext.setState(state => ({
        modalProps: {
          ...state.modalProps,
          open: false
        }
      }));

      return;
    }

    this.commonContext.setState(state => ({
      modalProps: {
        ...state.modalProps,
        ...CommonUtils.getModalProps(),
        qrCode: url,
        open: shouldOpenModal,
        isQRCodeMsg: isError ? this.commonContext.props.language.getMessage('header.qrCode.errorMsg') : ''
      }
    }));
  }

  /**
   * Initialize MixPanel Event Listener
   * This function create getMixPanelAdditionalParams global function for mixpanel event
   * commuincation bridge
   *
   */

  initializeMixPanelEventListener = () => {
    const self = this;
    window.getMixPanelAdditionalParams = () => {
      let baseFieldsData = {};
      try {
        const { course, match } = self.commonContext.props;
        const product = self.getProduct();
        const courseIdType = product.platform === BUNDLED_PLATFORM ? COURSE_TYPE_SMS : COURSE_TYPE_REGISTRAR;
        const { bundleId } = match?.params || {};

        if (self.location === EREADER) {
          baseFieldsData = {
            product_id: product.id || null,
            product_platform_code: product.platform || null,
            product_model_name: product.model || PRODUCT_MODEL,
            business_model_code: product?.subscription?.businessModelCode || null,
            book_id: product.bookID || product.id || null,
            pearson_subscription_id: product?.subscription?.subscriptionId || null,
            pearson_subscription_tier_code: product?.subscription?.entitlementLevel || null,
            pearson_subscription_tier: product?.subscription?.entitlementTier || null,
            course_id: course ? course.id : null,
            course_id_type: course ? courseIdType : null,
            course_section_id: course ? course.id : null,
            course_section_id_type: course ? courseIdType : null,
            course_section_title: product.title || null,
            channel_id: product.channel_id || null,
            entitlement_id: product?.subscription?.entitlementLevel || null,
            user_access_type: bundleId ? USER_ACCESS_TYPE_VALUES.DISAGGREGATED : USER_ACCESS_TYPE_VALUES.FULL
          };
        }
      } catch (e) {
        console.log('Mixpanel base fields update error', e);
      }

      return baseFieldsData;
    };
  }

  /**
   * Set or update current lang to html element lang attribute for a11y
   * @param {*} lang
   */
  updateLangAttribute(lang = null) {
    const { language } = this.commonContext.props;
    if (language && document.documentElement) {
      const currentLangage = document.documentElement.lang;
      const updateLanguage = lang || language.currentLanguage;
      if (currentLangage !== updateLanguage) {
        document.documentElement.lang = updateLanguage;
      }
    }
  }

  /**
   * Method to handle Integrated MLMLaunch page navgaivation
   * @param {*} pageId
   */

  handleIntegratedMLMLaunchMessage = (pageId) => {
    const { asset } = this.commonContext.props;
    if (pageId) {
      const chapterId = asset.getChapterId(pageId);
      const navData = chapterId ? { chapterId, action: null } : { };
      this.commonContext.handleNavigationChange(pageId, null, navData);
    }
  }

  /**
   * Sets whether it is a PPlus product and sets the appropriate
   * gps subscription of the product
   *
   */
  setGpsSubscriptionOfProduct = () => {
    const { product, course } = this.commonContext.props;
    if (product || course) {
      const actualProduct = product || course.getProduct();
      actualProduct.setIsPPlusProductAndGpsSubscriptionOfProduct();
    }
  }

  /**
   * Returns true if the product is PPlus
   *
   * @returns
   */
  isPPlusProduct = () => {
    const { product, course } = this.commonContext.props;

    return product
      ? product.getIsPPlusProduct()
      : course.getProduct().getIsPPlusProduct();
  }

  /**
   * To get product details
   *
   * @returns
   */
  getProduct = () => {
    const { product, course } = this.commonContext.props;
    let actualProduct = {};

    if (product || course) {
      actualProduct = product || course.getProduct();
    }

    return actualProduct;
  }

  /**
   * Returns true if the channel name exists for book
   *
   * @returns
   */
  hasChannelName = () => {
    let hasChannelName = false;
    const actualProduct = this.getProduct();
    hasChannelName = actualProduct && typeof actualProduct.channel_name === 'string' && actualProduct.channel_name.trim().length > 0;

    return hasChannelName;
  }

  /**
   * Trigger LD track event
   *
   * @param {*} name
   * @param {*} customFields
   */
  handleLDTrackEvent = (name, customFields = {}) => {
    const { featureFlagsClient } = this.commonContext.props;
    if (featureFlagsClient && name) {
      featureFlagsClient.track(name, customFields);
    }
  }

  getChapterId = () => {
    const chapterId = PathUtils.getQueryParameterbyName(CHAPTER_ID_QUERY_PARAM);

    return chapterId ? decodeURI(chapterId) : null;
  }

  /**
   * GA push for user Access eTextbook from diffrent source
   *
   */
  dispatchEreaderUserPathEvent = () => {
    const {
      AUTHHOME,
      CONSOLE,
      DIRECT,
      DEEPLINK
    } = SOURCE_OF_EREADER_LAUNCH;
    const { flags, assignmentsData } = this.commonContext.state;
    const { course } = this.commonContext.props;
    const isIntergratedMLMLaunch = CommonUtils.isIntergratedMLMLaunch();
    const isSourceFrom = PathUtils.getQueryParameterbyName(SOURCE_FROM);
    const product = this.getProduct();
    const businessModelCode = product.getBusinessModelCode();
    const primaryChannelId = product.getPrimaryChannelId();
    let userPath = null;
    let isConsoleDeeplink = false;
    const referrerURL = document.referrer;
    if (isSourceFrom) {
      try {
        const sourceFromValue = CommonUtils.getBase64Decoded(isSourceFrom);
        isConsoleDeeplink = sourceFromValue
          ? CommonUtils.isQueryParamAvailable(IS_CONSOLE, 'y', sourceFromValue) : false;
      } catch (error) {
        console.error('Error decoding source from', error);
      }
    }
    if (referrerURL) {
      if (this.isFromDeepLink) { // from deep link
        userPath = DEEPLINK;
      } else if (referrerURL.includes(env.CONSOLE_URL)
        || isConsoleDeeplink) { // from console
        userPath = CONSOLE;
      } else if (referrerURL.includes(`${env.CONTENT_HOST}/home`)
        || (this.redirectUrl && this.redirectUrl.includes(env.CONTENT_HOST))
      ) { // from authhome, For testing in local please change env.CONTENT_HOST into local url
        userPath = AUTHHOME;
      }
    } else {
      userPath = DIRECT; // from bookmark or copy paste url
    }
    const isShowAITutorEnabled = CommonUtils.isAITutorEnabled(flags.showAITutor, flags.isAIReviewUser, product.isAITutorSupported, true, {
      bookId: product.bookID,
      productId: product.id,
      AIEnableDynamicSummaryBookList: flags.AIEnableDynamicSummaryBookList,
      AIEnableDynamicSummary: flags.AIEnableDynamicSummary
    });
    const channelIds = CommonUtils.getCommaSeparatedChannelIds(
      product?.channel_id, product?.secondary_channels, product?.channel_mapping_type
    );
    const customFields = {
      book_id: CommonUtils.toString(product.bookID),
      business_model_code: CommonUtils.toString(businessModelCode),
      page_id: CommonUtils.toString(this.commonContext.pageId),
      user_path: userPath,
      gpt: (!isIntergratedMLMLaunch && isShowAITutorEnabled) ? 'true' : 'false',
      audio: (!isIntergratedMLMLaunch && product.isAudioBook(product.audioBook)) ? 'true' : 'false',
      channels_miniplayer: (!isIntergratedMLMLaunch && primaryChannelId && product.channel_name) ? 'true' : 'false',
      is_embedded: isIntergratedMLMLaunch ? 'true' : 'false',
      channels_sub_status: CommonUtils.channelsSubStatus(),
      product_mapping_type: CommonUtils.getChannelProductMappingType(product.channel_mapping_type),
      channel_type: channelIds || 'N/A',
      assignable_etext: product.isAETEnabled ? 'true' : 'false',
      assigned_etext: course?.isAet ? 'true' : 'false',
      count_assigned_assignments: assignmentsData?.assignments?.length || 0
    };
    CommonUtils.dispatchGaEvent(EREADER_CATEGORY, EVENT_EREADEREVENT, EREADER_ACTION_ACCESS_ETEXT, null, customFields);
  }


  /**
   * Function to initial and identify user in braze
   * @param {*} callBack
   */
  IntialiseAndIdentifyBraze = (callBack = () => {}, enableInAppMessage) => {
    const { user } = this.commonContext.props;
    if (!CommonUtils.isIntergratedMLMLaunch() && user && user.id) {
      BrazeUtils.handleEvent(BRAZE_IDENTIFY_USER, user.id, callBack, {}, enableInAppMessage);
      this.commonContext.setState({ brazeInitiated: true });
    }
  }

  /**
   * Get user details
   */
  getUserDetails = () => {
    const { user } = this.commonContext.props || {};
    const userStore = getSnapshot(user);
    const { email, firstName, lastName } = userStore;
    const fullName = `${firstName || ''} ${lastName || ''}`.trim();

    return {
      email,
      fullName
    };
  };

  /**
   * Set pendo user methods
   */
  setPendoUserMethods = () => {
    if (!window) {
      return;
    }

    window.getPendoUserDetails = this.getUserDetails;
  };

  /**
   * Function to initialize pendo or update the metadata of pendo
   *
   * @param {*} updateMetaData
   * @param {*} updateOptions
   */
  InitializePendo = (updateMetaData = false, updateOptions = {}) => {
    this.setPendoUserMethods();
    const { user } = this.commonContext.props;
    const { flags } = this.commonContext.state;
    const userStore = getSnapshot(user);
    let customFields = {};
    let commonFields = {
      pplus_user_role: userStore.userRole || NOT_AVAILABLE
    };

    if (this.location === AUTH_HOME || this.location === MLM_AUTH_HOME) {
      const herobanner = getSnapshot(Framework.getStoreRegistry().getStore('herobanner'));
      const heroBook = Framework.getStoreRegistry().getStore('herobanner').getHeroBook();

      customFields = {
        authHomePageTemplate: CommonUtils.getCurrentHeroTemplate(herobanner.bannerType),
        authHomeStudyPrep: CommonUtils.getChannelSubscriptionType(),
        oneReaderChannelID: herobanner.channelId || NOT_AVAILABLE,
        isAHExamPrepCardAtTop: false,
        bookId: heroBook?.book_id || NOT_AVAILABLE,
        courseId: heroBook?.course_id || NOT_AVAILABLE,
        isAetSessionTwo: false,
        hasAetNewStudents: false,
        hasAetNewPastDue: false,
        ...commonFields
      };
    } else if (this.location === EREADER) {
      const product = this.getProduct();
      const businessModelCode = product.getBusinessModelCode();
      customFields = {
        businessModelCode,
        oneReaderChannelID: product.channel_id || NOT_AVAILABLE,
        bookId: product.bookID || product.id || NOT_AVAILABLE,
        isChannelsSubscriber: CommonUtils.isChannelsSubscriber(),
        ...commonFields
      };
    }
    if (!CommonUtils.isIntergratedMLMLaunch() && user && user.id) {
      if (updateMetaData) {
        PendoUtils.updateOptions(user, this.location, { ...customFields, ...updateOptions });
      } else {
        PendoUtils.handleInitialization(user, this.location, customFields);
      }
    }
  }

  /**
   * Returns true if book has AI chatbot
   * @returns
   */
  isAIEnabled = () => {
    const { flags } = this.commonContext.state;
    const product = this.getProduct();

    return CommonUtils.isAITutorEnabled(flags.showAITutor, flags.isAIReviewUser, product.isAITutorSupported, true, {
      bookId: product.bookID,
      productId: product.id,
      AIEnableDynamicSummaryBookList: flags.AIEnableDynamicSummaryBookList,
      AIEnableDynamicSummary: flags.AIEnableDynamicSummary
    });
  }

  onBrazeEventFromReader = (name) => {
    if (name.toLowerCase() === AITutorLoaded.toLowerCase()) {
      this.commonContext.setState({ AITutorloaded: true });
    }
    const updatedBrazeContentCards = this.commonContext.state.brazeContentCards.map((card) => {
      if (card.data?.subscribeToEvent && name && card.data.subscribeToEvent.toLowerCase() === name.toLowerCase()) {
        return { ...card, data: { ...card.data, show: true } };
      }
      return card;
    });
    if (this.commonContext.state.brazeContentCards && this.commonContext.state.brazeContentCards.length > 0) {
      this.commonContext.setState({ brazeContentCards: updatedBrazeContentCards });
    }
  }

  /**
   * Update the pendo meta data from VR callback
   *
   * @param {*} data
   */
  onPendoMetaFromReader = (data) => {
    const { user } = this.commonContext.props;
    if (data && !CommonUtils.isEmpty(data) && data.pendoMetadata) {
      if (!CommonUtils.isIntergratedMLMLaunch() && user && user.id) {
        this.InitializePendo(true, data.pendoMetadata);
      }
    }
  }

  /**
   * Enables archive title feature when
   * 1. Flag - enableArchival = true
   * 2. User books >= archiveWhenUserHasXOrMoreTitles
   * 3. User does not have MULTI subscription
   *
   */
  setEnableTitleArchival = () => {
    const { bookshelf } = this.commonContext.props;
    const { flags } = this.commonContext.state;
    const { enableArchival, archiveWhenUserHasXOrMoreTitles } = flags;
    const books = getSnapshot(bookshelf)?.books;
    const hasArchivedTitles = books.some(book => book.isArchived);
    let enableTitleArchival = false;

    if (enableArchival && (hasArchivedTitles || (books.length >= archiveWhenUserHasXOrMoreTitles))) {
      enableTitleArchival = true;
      this.commonContext.setState({ enableTitleArchival: true });
    }

    bookshelf.setShowArchiveOrUnarchive(enableTitleArchival);
  }

  /**
   * Function to open component from braze campaign
   * @param {*} component
   */
  openEreaderComponentFromBraze = (component, utmParams = {}) => {
    if (component && BRAZE_EREADER_COMPONENTS[component.toUpperCase()]) {
      const {
        AUDIO, CHANNELS, STUDY, NOTEBOOK
      } = BRAZE_EREADER_COMPONENTS;
      const { drawerView } = this.commonContext.state;
      const eventData = CommonUtils.isEmpty(utmParams) ? {} : { eventData: utmParams };
      switch (component.toUpperCase()) {
        case AUDIO:
          this.commonContext.setState({
            drawerView: {
              ...drawerView,
              ...eventData,
              drawerState: DRAWER_STATE_OPEN,
              selectedPanel: DRAWER_PANEL_AUDIO
            }
          });
          break;
        case CHANNELS:
          this.commonContext.setState({
            drawerView: {
              ...drawerView,
              ...eventData,
              drawerState: DRAWER_STATE_OPEN,
              selectedPanel: DRAWER_PANEL_CHANNELS
            }
          });
          break;
        case STUDY:
          this.commonContext.setState({
            drawerView: {
              ...drawerView,
              ...eventData,
              drawerState: DRAWER_STATE_OPEN,
              selectedPanel: DRAWER_PANEL_STUDY
            }
          });
          break;
        case NOTEBOOK:
          this.commonContext.setState({
            drawerView: {
              ...drawerView,
              ...eventData,
              drawerState: DRAWER_STATE_OPEN,
              selectedPanel: DRAWER_PANEL_NOTEBOOK
            }
          });
          break;
        default:
          break;
      }
    }
  }

  /**
   * To get AI turor auto open command
   *
   * @param {*} data
   * @returns
   */

  getAiTutorAutoOpenCommands(data) {
    const renderData = !CommonUtils.isEmpty(data) ? data : {};
    const { titles = [] } = renderData;
    let openCommands = null;
    if (titles.length > 0) {
      const { bookID, id } = this.getProduct();
      const bookIdToMatch = bookID || id;
      const result = titles.filter(({ bookId }) => (bookIdToMatch === bookId));
      openCommands = result.length > 0 ? result[0] : null;
    }

    return openCommands;
  }

  /**
   * Gets and returns print page number from query parameter
   *
   * @returns
   */
  getPrintPageNumber = () => {
    const printPageNumber = PathUtils.getQueryParameterbyName(GO_TO_PAGENO_QUERY_PARAM);

    return printPageNumber ? decodeURI(printPageNumber) : null;
  }

  /**
   * Gets and returns source from query parameter
   *
   * @returns
   */
  getSourceFrom = () => {
    const sourceFrom = PathUtils.getQueryParameterbyName(SOURCE_FROM);

    return sourceFrom ? decodeURI(sourceFrom) : null;
  }

  /**
   *  IS printable book or not
   *
   * @returns
   */
  isPrintEnabledForBook() {
    const { configuration, model } = this.getProduct();
    const isPdfConfiguration = !!(configuration?.headerFeatures?.hasPrintLink);
    const isCongifuration = !!(configuration?.enableUserPrinting);
    const isCustomCollections = model === CUSTOM_COLLECTIONS;

    return isPdfConfiguration || isCongifuration || isCustomCollections;
  }

  /**
   * Print page message while print via browser
   *
   * @returns
   */
  getPrintMessage() {
    const message = {};

    if (this.isPrintEnabledForBook()) {
      message.title = this.commonContext.props.language.getMessage('print.printAvailTitle');
      // eslint-disable-next-line max-len
      message.description = this.commonContext.props.language.getMessage('print.printAvailDesc');
      message.illustration = PRINT_PAGE_ILLUSTRATION;
    } else {
      message.title = this.commonContext.props.language.getMessage('print.printNotAvailTitle');
      // eslint-disable-next-line max-len
      message.description = this.commonContext.props.language.getMessage('print.printNotAvailDesc');
      message.component = (
        <QRCodePrintMessage
          language={this.commonContext.props.language}
          qrCode={env.APPSFLYERS_STATIC_URL}
        />
      );
    }

    return message;
  }

  /**
   * Print message component while print via browser
   *
   * @returns
   */
  renderPrintMessage() {
    const {
      title,
      description,
      illustration,
      component
    } = this.getPrintMessage();

    return (
      <PrintMessage
        title={title}
        description={description}
        illustration={illustration}
        component={component}
      />
    );
  }

  /**
   * To initiate DeviceManagement On Ereader for HEI
   * If user has GPS subscrioption then this won't initiate again
   *
   */
  initiateDeviceManagementOnEreader() {
    const { user, course } = this.commonContext.props;
    const product = this.getProduct();
    const businessModelCode = product.getBusinessModelCode();
    const hasCourseId = !!course?.courseId && !course.isCourseExpired();
    if (CommonUtils.isPplusOrIAbookOrHEI(businessModelCode, hasCourseId)) {
      Framework.initiateDeviceManagement(user.isImpersonatedId);
    }
  }

  /**
   * DSM flow activation in ereader
   *
   */
  deviceManagemenFlowOnEreader() {
    const { user, device, product, course } = this.commonContext.props;
    const {
      flags,
      isDeviceIncluded
    } = this.commonContext.state;
    const isIntergratedMLMLaunch = CommonUtils.isIntergratedMLMLaunch();
    if (!user.isImpersonatedId && !isIntergratedMLMLaunch) {
      // Avoid impersonated users from device count
      const deviceId = window.piSession && window.piSession.getDeviceId();
      // Device List listener from framework
      const businessModelCode = this.getProduct().getBusinessModelCode();
      const hasCourseId = !!course?.courseId && !course.isCourseExpired();
      // DM enabled for DTC, IA and HEI
      if (CommonUtils.isPplusOrIAbookOrHEI(businessModelCode, hasCourseId) && !isDeviceIncluded) {
        /**
         * Thread level check only for product router
         */
        // Thread level warn START
        if (product) {
          if (flags.enableAccountAbuseLevel1 || flags.enableAccountAbuseLevel2) {
            // Request threatStatus
            Framework.getEventManager().publish(IES_DEVICE_LIST_REQUESTED, {
              type: 'threatStatus',
              data: { deviceId }
            });
          }
          /**
           * Listner of threatStatus --> threatLevel 1 warning, threatLevel 2 accountLock
           * threatLevel 1 warning on 5 concurrent session login
           * threatLevel 2 account lock after 5 concurrent session login
           */
          Framework.getEventManager().on(THREAT_STATUS_SUCCESS, (data) => {
            if (data.threatLevel === 1 && !data.hasUserWarningDisplayed && flags.enableAccountAbuseLevel1) {
              this.commonContext.setState({
                threatLevel: 1
              });
              Framework.getEventManager().publish(IES_DEVICE_LIST_REQUESTED, {
                type: 'warningDisplayed'
              });
            } else if (data.threatLevel === 2 && flags.enableAccountAbuseLevel2) {
              this.commonContext.setState({
                threatLevel: 2
              });
              Framework.getEventManager().on(ACCOUNT_LOCK_SUCCESS, () => {
                CommonUtils.dispatchGaEvent(
                  OTP_MODAL,
                  LOCK_ACCOUNT_EVENT_NAME,
                  LOCK_ACCOUNT_EVENT_ACTION,
                  LOCK_ACCOUNT_EVENT_LABEL
                );
              });
              Framework.getEventManager().publish(IES_DEVICE_LIST_REQUESTED, {
                type: 'accountLock',
                data: {
                  deviceId,
                  userId: user.id
                }
              });
            }
          });
        }
        // Thread level warn END
        // Added device id for incase of disconnect request
        this.commonContext.setState({ addedDeviceId: deviceId });
        // check this device id is already in list
        if (deviceId && !this.commonContext.isDeviceIdInList(deviceId)) {
          // check if limit reached
          if (device.limitReached && SHOW_DEVICE_MANAGEMENT_POPUP) {
            this.commonContext.userLimitExceed = true;
            setTimeout(() => {
              const elements = document.getElementsByClassName('pageNavigation');
              if (elements.length) {
                elements[0].style.display = 'none';
              }
            }, 3000);
            // call open popup
            this.commonContext.setState({ open: true });
          } else {
            // Register the device
            Framework.getEventManager().publish(IES_DEVICE_LIST_REQUESTED, {
              type: 'register',
              payload: deviceId
            });
          }
        }
        if (flags.deviceManagementViolation) {
          Framework.getEventManager().on(DEVICE_REGISTER_SUCCESS, () => {
            Framework.getEventManager().publish(IES_DEVICE_LIST_REQUESTED, {
              type: 'violation',
              data: { deviceId }
            });
          });
          if (deviceId && this.commonContext.isDeviceIdInList(deviceId)) {
            Framework.getEventManager().publish(IES_DEVICE_LIST_REQUESTED, {
              type: 'violation',
              data: { deviceId }
            });
          }
          Framework.getEventManager().on(DEVICE_VIOLATION_SUCCESS, (data) => {
            if (data.isWebViolation) {
              Framework.getEventManager().publish(IES_DEVICE_LIST_REQUESTED, {
                type: 'generateOTP',
                data: {
                  clientId: env.CLIENT_ID,
                  deviceId
                }
              });
              CommonUtils.deviceManagementReSentOTPEvents(SEND_OTP);
            }
            this.commonContext.setState({
              isLimitExceeded: data.isWebViolation
            });
          });
        }
        Framework.getEventManager().publish(IES_DEVICE_LIST_REQUESTED, {
          type: 'list',
          payload: { includeDeviceDetails: true }
        });
        this.commonContext.setState({
          isDeviceIncluded: true
        });
      }
    }
  }

  /**
   * Handle popular channel config
   *
   */
  getPopularChannelsConfig = () => {
    const herobanner = getSnapshot(Framework.getStoreRegistry().getStore('herobanner'));
    const config = {
      showCheckMark: this.location === AUTH_HOME && herobanner.bannerType !== HERO_BANNER_TYPES.MARKETING_TEMPLATE,
      showAddButton: this.location === AUTH_HOME,
      showSubscriptionStatus: CommonUtils.isChannelsSubscriber()
    };

    return config;
  };

  /**
   * Return variant v1 or default based on the new UI or Old UI
   *
   * @returns
   */
  // eslint-disable-next-line class-methods-use-this
  getVariant = () => DEFAULT_TEMPLATE;

  /**
   * Method is used to create the popular channel filter list
   * @param {*} channelCards
   * @param {*} filterId
   * @param {*} isFromAddCourse
   * @returns
   */
  // eslint-disable-next-line class-methods-use-this
  getPopularChannelsFilterData = (channelCards, filterId, isFromAddCourse = false) => {
    if (channelCards?.length <= 0) return null;
    const channelGroup = [];
    let isRecommended = false;
    const filterList = [];
    let popularChannels = CommonUtils.getPopularChannels(channelCards);
    popularChannels = popularChannels.filter(card => !this.commonContext.globalDisabledChannelIds.includes(card.channelId));
    const allChannels = { id: CHANNEL_FILTER.ALL_CHANNELS, title: `${this.commonContext.props.language.getMessage('popularChannelSlider.allChannels')} (${popularChannels?.length})`, isSelected: filterId === CHANNEL_FILTER.ALL_CHANNELS };

    filterList.push(allChannels);

    const channelsFilters = popularChannels.map((card) => {
      if (card.recommended) isRecommended = true;

      const filter = {};
      if (!channelGroup.includes(card?.group)) {
        filter.id = card.group;
        filter.title = card.group;
        filter.isSelected = filterId === filter.id;
      }
      channelGroup.push(card?.group);
      return filter;
    }).filter(card => card?.id).sort((a, b) => a.id.localeCompare(b.id));
    filterList.push(...channelsFilters);

    if (isRecommended) filterList.splice(1, 0, { id: CHANNEL_FILTER.RECOMMENDED, title: this.commonContext.props.language.getMessage('popularChannelSlider.recommended'), isSelected: filterId === CHANNEL_FILTER.RECOMMENDED });
    if (!filterList?.some(filter => filter.isSelected)) {
      filterList[0].isSelected = true;
      this.setPopulatChannelFilter(filterList[0]?.id, isFromAddCourse);
    }

    return filterList;
  };

  /**
   * Method is used to get the PopularChannel list in sorted order
   * @param {*} channelCards
   * @param {*} filterId
   * @returns
   */

  getSortedPopularChannels = (channelCards, filterId) => {
    let popularChannels = CommonUtils.getPopularChannels(channelCards);
    popularChannels = popularChannels.filter(card => !this.commonContext.globalDisabledChannelIds.includes(card.channelId));

    if (filterId !== CHANNEL_FILTER.ALL_CHANNELS) {
      if (filterId === CHANNEL_FILTER.RECOMMENDED) {
        return popularChannels.map(obj => ({ ...obj, isFiltered: obj.recommended || false }));
      }

      return popularChannels.map((obj) => {
        const filter = obj.group === filterId;
        return { ...obj, isFiltered: filter };
      }).sort((a, b) => b.active - a.active);
    }

    return popularChannels.map(obj => ({ ...obj, isFiltered: true })).sort((a, b) => b.active - a.active);
  };

  /**
   * If this is comming from addCourseFlow we are updating channelCard
   *
   * @param {*} itemId
   * @param {*} isFromAddCourse
   */

  setPopulatChannelFilter = (itemId, isFromAddCourse = false) => {
    const currentFilterSelection = isFromAddCourse
      ? { addCourseFilter: itemId } : { currentPopularChannelFilter: itemId };


    this.commonContext.setState({
      ...currentFilterSelection
    }, () => {
      if (isFromAddCourse) {
        let { onboardSlideData } = this.commonContext.state;
        onboardSlideData = onboardSlideData.map((slide) => {
          const renderSlide = this.commonContext.onboardingPlugin.updateChannelData(
            slide,
            null
          );
          return renderSlide;
        });

        this.commonContext.setState({ onboardSlideData });
      }
    });
  };


  /**
   * Method to check hero book is nest test access or not
   *
   */
  isNestTestAccess = () => {
    const { flags = {} } = this.commonContext?.state || {};
    const heroBook = this.commonContext.heroDashboardPlugin.getHeroBook();
    const isHEIUser = CommonUtils.isHEIUser();
    return flags.enableNestTestPromo && !isHEIUser && heroBook && heroBook?.isNestTest;
  };

  /**
   * Method used to get InitiatePoint
   *
   * @param {*} fromLocation
   */
  getInitiatePoint = (fromLocation) => {
    const currentLocation = this.location === AUTH_HOME ? LOCATION_IN_APP.HOME1 : LOCATION_IN_APP.HOME2;

    return fromLocation === LOCATION_IN_APP.LIBRARY_L2 ? LOCATION_IN_APP.LIBRARYL2 : currentLocation;
  };

  /**
   * Method to get InitiatePoint params from URL
   *
   * @param {*} fromLocation
   */
  // eslint-disable-next-line class-methods-use-this
  getInitiatePointFromQueryParam = () => {
    const initiatePoint = PathUtils.getQueryParameterbyName(INITIATE_POINT_QUERY_PARAM);

    return initiatePoint ? decodeURI(initiatePoint) : null;
  };

  /**
   * Returns true if Global Nav is enabled
   *
   * @returns {Boolean}
   */
  isIntegratedUI = () => {
    if (this.location === EREADER) {
      const {
        isGlobalNavEnabled
      } = this.commonContext.props || {};

      return isGlobalNavEnabled;
    }

    const { enableGlobalNav = false } = this.commonContext?.state.flags || {};

    return enableGlobalNav;
  };

  backToHome = () => {
    if (this.isIntegratedUI()) {
      document.title = CommonUtils.getAppTitle();
      const sourceFrom = CommonUtils.getBase64Decoded(this.commonContext.sourceFrom);
      CommonUtils.backToHome(sourceFrom);
    } else {
      this.commonContext.handleBackNavClick();
    }
  };

  /**
 * Cleanup AssignmentFetcher when component unmounts
 */
  cleanupAssignmentFetcher = () => {
    if (this.assignmentFetcher) {
      this.assignmentFetcher.cleanup();
      console.log('AssignmentFetcher cleaned up');
    }
  };

  /**
   * Gets and returns print page number from query parameter
   *
   * @returns
   */
  // eslint-disable-next-line class-methods-use-this
  getAssignmentId = () => {
    const assignmentId = PathUtils.getQueryParameterbyName(ASSIGNMENT_ID_QUERY_PARAM);

    return assignmentId ? decodeURI(assignmentId) : null;
  };

  /**
   * Method to get tracking global data for Ereader
   *
   * @returns
   */
  getTrackingGlobalData = () => {
    const product = this.getProduct();
    const { course } = this.commonContext.props;

    return {
      person_role_code:
       CommonUtils.getIsStudent({ courseUserRole: course?.role || product?.role }) ? 'student' : 'instructor',
      course_id: course?.id || null,
      course_section_id: course?.id || null,
      organization_id: course?.organizationId || null,
      lms_platform: null,
      access_point: null,
      assignable_etext: product.isAETEnabled ? 'true' : 'false',
      assigned_etext: course?.isAet ? 'true' : 'false'
    };
  };

  /**
   * Method to handle AET preference change
   */
  handleAetPreferenceChange = (preferenceType, isEnabled) => {
    const { userBookPreference } = this.commonContext.state;
    if (ETEXT_PREFERENCE_SOURCE[preferenceType]) {
      userBookPreference[ETEXT_PREFERENCE_SOURCE[preferenceType]] = isEnabled;
    }
    this.commonContext.setState({ userBookPreference });
  };
}
